import React, { useState, useEffect } from 'react';
import { getConnectionSchema } from '../services/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faChevronRight,
  faDatabase,
  faFileLines,
  faTable,
  faLink,
} from '@fortawesome/free-solid-svg-icons';

const getConnectionIcon = (type) => {
  const t = (type || '').toLowerCase();
  if (t === 'csv') return faFileLines;
  if (t === 'excel') return faTable;
  return faDatabase;
};

export default function RightPanel({ connection, activeMode, relationSession }) {
  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    if (activeMode === 'relation') {
      setSchema(null);
      setLoading(false);
      setExpanded(() => {
        const first = relationSession ? 'table1' : null;
        return first ? { [first]: true } : {};
      });
      return;
    }
    if (!connection) { setSchema(null); return; }
    setLoading(true);
    getConnectionSchema(connection.id)
      .then(data => {
        setSchema(data.schema);
        // Expand first table
        const first = Object.keys(data.schema)[0];
        if (first) setExpanded({ [first]: true });
      })
      .catch(() => setSchema(null))
      .finally(() => setLoading(false));
  }, [connection?.id, activeMode, relationSession?.id]);

  if (activeMode === 'relation') {
    return (
      <div style={s.panel}>
        <div style={s.header}>
          <span style={s.headerTitle}>Relation Schema</span>
          {relationSession ? (
            <span style={s.connBadge}>
              <FontAwesomeIcon icon={faLink} aria-hidden="true" />
              join key: {relationSession.join_key}
            </span>
          ) : (
            <span style={s.connBadge}>Waiting for upload</span>
          )}
        </div>
        <div style={s.body}>
          {!relationSession ? (
            <div style={s.empty}>
              <FontAwesomeIcon icon={faTable} style={{ fontSize: 32 }} aria-hidden="true" />
              <span>Relation schema appears here</span>
            </div>
          ) : (
            <>
              {[
                { key: 'table1', name: relationSession.table1_name, columns: relationSession.columns1 || [] },
                { key: 'table2', name: relationSession.table2_name, columns: relationSession.columns2 || [] },
              ].map(table => (
                <div key={table.key} style={s.tableCard}>
                  <button
                    onClick={() => setExpanded(e => ({ ...e, [table.key]: !e[table.key] }))}
                    style={s.tableHeader}
                  >
                    <span style={s.tableIcon}><FontAwesomeIcon icon={faTable} aria-hidden="true" /></span>
                    <span style={s.tableName}>{table.name}</span>
                    <span style={s.colCount}>{table.columns.length}</span>
                    <span style={s.chevron}>
                      <FontAwesomeIcon icon={expanded[table.key] ? faChevronDown : faChevronRight} aria-hidden="true" />
                    </span>
                  </button>
                  {expanded[table.key] && (
                    <div style={s.colList}>
                      {table.columns.map(col => (
                        <div key={col} style={s.colRow}>
                          <span style={s.colName}>{col}</span>
                          <span style={{ ...s.colType, color: 'var(--text-muted)' }}>col</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </>
          )}
        </div>
        <div style={s.footer}>
          <div style={s.footerRow}>
            <span style={s.footerLabel}>Mode</span>
            <span style={s.footerVal}>relation</span>
          </div>
          <div style={s.footerRow}>
            <span style={s.footerLabel}>Join Key</span>
            <span style={s.footerVal}>{relationSession?.join_key || '-'}</span>
          </div>
        </div>
      </div>
    );
  }

  if (!connection) return (
    <div style={s.panel}>
      <div style={s.empty}>
        <FontAwesomeIcon icon={faTable} style={{ fontSize: 32 }} aria-hidden="true" />
        <span>Schema appears here</span>
      </div>
    </div>
  );

  const typeColor = (t) => {
    t = (t || '').toLowerCase();
    if (t.includes('int') || t.includes('num') || t.includes('float') || t.includes('double') || t.includes('decimal')) return 'var(--info)';
    if (t.includes('bool')) return 'var(--success)';
    if (t.includes('date') || t.includes('time')) return '#a855f7';
    return 'var(--text-muted)';
  };

  const typeAbbr = (t) => {
    t = (t || '').toLowerCase();
    if (t.includes('character varying') || t.includes('varchar')) return 'str';
    if (t.includes('integer') || t.includes('int4')) return 'int';
    if (t.includes('bigint') || t.includes('int8')) return 'i64';
    if (t.includes('float') || t.includes('double')) return 'f64';
    if (t.includes('numeric') || t.includes('decimal')) return 'dec';
    if (t.includes('boolean')) return 'bool';
    if (t.includes('timestamp')) return 'ts';
    if (t.includes('date')) return 'date';
    if (t.includes('text')) return 'text';
    if (t.length > 6) return t.slice(0, 5);
    return t;
  };

  return (
    <div style={s.panel}>
      <div style={s.header}>
        <span style={s.headerTitle}>Schema</span>
          <span style={s.connBadge}>
          <FontAwesomeIcon icon={getConnectionIcon(connection.type)} aria-hidden="true" />
          {connection.name}
        </span>
      </div>

      <div style={s.body}>
        {loading && (
          <div style={s.loadingWrap}>
            <span style={s.spinner} />
            <span>Loading schema…</span>
          </div>
        )}

        {!loading && schema && Object.keys(schema).length === 0 && (
          <div style={s.noSchema}>No tables found</div>
        )}

        {!loading && schema && Object.keys(schema).map(table => (
          <div key={table} style={s.tableCard}>
            <button
              onClick={() => setExpanded(e => ({ ...e, [table]: !e[table] }))}
              style={s.tableHeader}
            >
              <span style={s.tableIcon}><FontAwesomeIcon icon={faTable} aria-hidden="true" /></span>
              <span style={s.tableName}>{table}</span>
              <span style={s.colCount}>{schema[table].length}</span>
              <span style={s.chevron}>
                <FontAwesomeIcon icon={expanded[table] ? faChevronDown : faChevronRight} aria-hidden="true" />
              </span>
            </button>
            {expanded[table] && (
              <div style={s.colList}>
                {schema[table].map(col => (
                  <div key={col.column} style={s.colRow}>
                    <span style={s.colName}>{col.column}</span>
                    <span style={{ ...s.colType, color: typeColor(col.type) }}>
                      {typeAbbr(col.type)}
                    </span>
                    {!col.nullable && <span style={s.reqTag}>req</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Connection info */}
      <div style={s.footer}>
        <div style={s.footerRow}>
          <span style={s.footerLabel}>Type</span>
          <span style={s.footerVal}>{connection.type}</span>
        </div>
        <div style={s.footerRow}>
          <span style={s.footerLabel}>ID</span>
          <span style={s.footerVal}>#{connection.id}</span>
        </div>
        <div style={s.footerRow}>
          <span style={s.footerLabel}>Created</span>
          <span style={s.footerVal}>{new Date(connection.created_at).toLocaleDateString()}</span>
        </div>
      </div>
    </div>
  );
}

const s = {
  panel: {
    width: 240, flexShrink: 0, height: '100vh', background: 'var(--bg-secondary)',
    borderLeft: '1px solid var(--border)', display: 'flex', flexDirection: 'column',
    overflow: 'hidden', animation: 'fadeInRight 0.3s ease',
  },
  header: {
    padding: '16px 14px 12px', borderBottom: '1px solid var(--border-subtle)',
    display: 'flex', flexDirection: 'column', gap: 6,
  },
  headerTitle: { fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', letterSpacing: '0.8px', textTransform: 'uppercase' },
  connBadge: { fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 5 },
  body: { flex: 1, overflowY: 'auto', padding: '10px 10px' },
  empty: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
    padding: '40px 16px', color: 'var(--text-muted)', fontSize: 12,
  },
  loadingWrap: {
    display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)',
    fontSize: 12, padding: '20px 4px',
  },
  spinner: {
    width: 14, height: 14, border: '2px solid var(--border)',
    borderTopColor: 'var(--accent)', borderRadius: '50%',
    animation: 'spin 0.7s linear infinite', display: 'inline-block',
  },
  noSchema: { fontSize: 12, color: 'var(--text-muted)', padding: '16px 4px' },
  tableCard: {
    borderRadius: 10, border: '1px solid var(--border-subtle)',
    marginBottom: 8, overflow: 'hidden', animation: 'fadeIn 0.3s ease',
  },
  tableHeader: {
    width: '100%', display: 'flex', alignItems: 'center', gap: 7,
    padding: '8px 10px', background: 'var(--bg-tertiary)', border: 'none',
    cursor: 'pointer', textAlign: 'left', transition: 'background 0.15s',
  },
  tableIcon: { fontSize: 12, color: 'var(--accent)' },
  tableName: { flex: 1, fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  colCount: {
    fontSize: 10, background: 'var(--bg-active)', color: 'var(--text-muted)',
    borderRadius: 5, padding: '1px 5px', fontWeight: 600,
  },
  chevron: { fontSize: 10, color: 'var(--text-muted)' },
  colList: { padding: '4px 0' },
  colRow: {
    display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px',
    borderBottom: '1px solid var(--border-subtle)',
  },
  colName: { flex: 1, fontSize: 11, color: 'var(--text-secondary)', fontFamily: 'var(--font-mono)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  colType: { fontSize: 10, fontFamily: 'var(--font-mono)', fontWeight: 600, flexShrink: 0 },
  reqTag: {
    fontSize: 9, background: 'var(--error-subtle)', color: 'var(--error)',
    borderRadius: 4, padding: '1px 4px', fontWeight: 700, letterSpacing: '0.3px',
  },
  footer: { padding: '12px 14px', borderTop: '1px solid var(--border-subtle)' },
  footerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 },
  footerLabel: { fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px' },
  footerVal: { fontSize: 11, color: 'var(--text-secondary)', fontFamily: 'var(--font-mono)' },
};
